import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, Gavel, MapPin, Clock, ChevronRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import api from "@/lib/api";
import { fmtDate, fmtTime } from "@/components/PageHeader";

export function UpcomingEvents({ endpoint, to }) {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get(endpoint).then(({ data }) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const upcoming = (data.items || [])
        .filter((it) => it.event_date && new Date(`${it.event_date.slice(0, 10)}T00:00:00`) >= today)
        .sort((a, b) => (a.event_date || "").localeCompare(b.event_date || "") || (a.start_time || "").localeCompare(b.start_time || ""))
        .slice(0, 2);
      setItems(upcoming);
    }).catch(() => setItems([]));
  }, [endpoint]);

  const Header = to ? Link : "div";
  const headerProps = to ? { to, "data-testid": "upcoming-events-link" } : {};

  return (
    <Card className="mt-6 p-6 border-slate-200 shadow-sm" data-testid="upcoming-events-card">
      <Header {...headerProps} className={`mb-4 flex items-center gap-2 ${to ? "group cursor-pointer" : ""}`}>
        <CalendarClock className="h-5 w-5 text-slate-400" />
        <h3 className="text-lg font-semibold text-slate-800 font-heading">Upcoming events</h3>
        {to && <ChevronRight className="h-4 w-4 text-slate-400 transition-transform group-hover:translate-x-0.5" />}
      </Header>
      {items === null ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-400" data-testid="upcoming-events-empty">No upcoming events.</p>
      ) : (
        <div className="divide-y divide-slate-100" data-testid="upcoming-events-list">
          {items.map((it) => (
            <div key={it.id} className="flex items-start gap-4 py-3" data-testid={`upcoming-item-${it.id}`}>
              <div className="w-24 shrink-0">
                <p className="text-sm font-bold text-slate-800">{fmtDate(it.event_date)}</p>
                {it.all_day ? <p className="flex items-center gap-1 text-xs text-slate-500"><Clock className="h-3 w-3" /> All day</p> : it.start_time && <p className="flex items-center gap-1 text-xs text-slate-500 tabular-nums"><Clock className="h-3 w-3" /> {fmtTime(it.start_time)}</p>}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  {it.type === "conference" && <Gavel className="h-3.5 w-3.5 shrink-0 text-blue-500" />}
                  <p className="font-semibold text-slate-800">{it.title}</p>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${it.type === "conference" ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"}`}>
                    {it.type === "conference" ? "Conference" : "Event"}
                  </span>
                </div>
                {it.location && <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-500"><MapPin className="h-3 w-3" /> {it.location}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export default UpcomingEvents;
